import React, { useEffect, useRef, useState } from "react";

// ─── Constants ────────────────────────────────────────────────────────────────

const WORDS = [
  "APPLE", "TIGER", "MOON", "PYTHON", "REACT", "MEMORY",
  "BRAIN", "FOCUS", "CYBER", "AI", "MACHINE", "LEARNING",
];

const ACHIEVEMENT_LIST = {
  first_correct:  "First Correct Answer",
  five_correct:   "5 Correct Answers",
  ten_correct:    "10 Correct Answers",
  level_10:       "Level 10 Reached",
  perfect_recall: "Perfect Recall",
  memory_master:  "Memory Master",
};

// ─── Reusable Components ──────────────────────────────────────────────────────

function StatPill({ label, value, colorClass }) {
  return (
    <div className={`${colorClass} rounded-2xl px-4 py-3 flex flex-col items-center gap-0.5`}>
      <span className="text-xs font-semibold uppercase tracking-widest opacity-70">{label}</span>
      <span className="text-2xl font-black leading-none">{value}</span>
    </div>
  );
}

function SectionCard({ children, className }) {
  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-5 ${className || ""}`}>
      {children}
    </div>
  );
}

function ModeButton({ active, color, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all border-2 ${
        active
          ? `${color} border-transparent text-white shadow-md scale-105`
          : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
      }`}
    >
      {children}
    </button>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function MemoryGame() {
  const [mode, setMode] = useState("number");
  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);

  const [sequence, setSequence] = useState("");
  const [showSequence, setShowSequence] = useState(false);
  const [input, setInput] = useState("");
  const [message, setMessage] = useState("");
  const [timer, setTimer] = useState(0);

  const [bestScore, setBestScore] = useState(
    Number(localStorage.getItem("memory_best_score")) || 0
  );

  // Custom Words
  const [customWordsInput, setCustomWordsInput] = useState("");
  const [customWords, setCustomWords] = useState([]);
  const [customWarning, setCustomWarning] = useState("");

  // Paragraph Recall
  const [paragraphInput, setParagraphInput] = useState("");
  const [savedParagraph, setSavedParagraph] = useState("");
  const [paragraphWords, setParagraphWords] = useState([]);
  const [paragraphRound, setParagraphRound] = useState(1);
  const [paragraphPhase, setParagraphPhase] = useState("input");
  const [userParagraphInput, setUserParagraphInput] = useState("");

  // Stats
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);

  // Achievements
  const [achievements, setAchievements] = useState(() => {
    try { return JSON.parse(localStorage.getItem("memory_achievements") || "{}"); }
    catch { return {}; }
  });
  const [achievementMsg, setAchievementMsg] = useState("");

  const showTimeoutRef        = useRef(null);
  const roundTimeoutRef       = useRef(null);
  const achievementTimeoutRef = useRef(null);

  // ── Generators ──────────────────────────────────────────────────────────────

  const generateNumberSequence = () => {
    let r = "";
    for (let i = 0; i < level + 2; i++) r += Math.floor(Math.random() * 10);
    return r;
  };

  const generateWordSequence = () => {
    const count = Math.min(level + 1, 6);
    return [...WORDS].sort(() => Math.random() - 0.5).slice(0, count).join(" ");
  };

  const generateCustomWordSequence = () => {
    const count = Math.min(level + 1, 6, customWords.length);
    return [...customWords].sort(() => Math.random() - 0.5).slice(0, count).join(" ");
  };

  // ── Achievements ─────────────────────────────────────────────────────────────

  const unlockAchievement = (key) => {
    if (achievements[key]) return;
    const updated = { ...achievements, [key]: true };
    setAchievements(updated);
    localStorage.setItem("memory_achievements", JSON.stringify(updated));
    setAchievementMsg(`🏆 Achievement Unlocked: ${ACHIEVEMENT_LIST[key]}`);
    if (achievementTimeoutRef.current) clearTimeout(achievementTimeoutRef.current);
    achievementTimeoutRef.current = setTimeout(() => setAchievementMsg(""), 3000);
  };

  useEffect(() => {
    if (level === 10 && !achievements.level_10) unlockAchievement("level_10");
  }, [level]);

  // ── Round logic ──────────────────────────────────────────────────────────────

  const startRound = () => {
    let generated = "";
    if (mode === "number") {
      generated = generateNumberSequence();
    } else if (mode === "word") {
      generated = generateWordSequence();
    } else if (mode === "custom") {
      const required = Math.min(level + 1, 6);
      if (customWords.length < required) {
        setCustomWarning(`⚠ Need at least ${required} custom words for level ${level}.`);
        setSequence(""); setShowSequence(false); return;
      }
      setCustomWarning("");
      generated = generateCustomWordSequence();
    } else {
      return;
    }

    setSequence(generated);
    setInput("");
    setMessage("");
    setShowSequence(true);
    const displayTime = Math.max(3000, 7000 - level * 200);
    if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
    showTimeoutRef.current = setTimeout(() => {
      setShowSequence(false);
      setTimer(15);
    }, displayTime);
  };

  useEffect(() => {
    if (mode === "paragraph") { setParagraphPhase("input"); setMessage(""); return; }
    startRound();
  }, [level, mode]);

  useEffect(() => {
    if (mode === "custom") startRound();
  }, [customWords]);

  // ── Timer ────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const active = mode === "paragraph"
      ? paragraphPhase === "recall"
      : (!showSequence && !!sequence);
    if (!active || timer <= 0) return;
    const interval = setInterval(() => setTimer(p => p - 1), 1000);
    return () => clearInterval(interval);
  }, [timer, showSequence, paragraphPhase, mode, sequence]);

  useEffect(() => {
    if (timer !== 0) return;
    if (mode === "paragraph") {
      if (paragraphPhase === "recall") handleParagraphRoundWrong("⏰ Time Up!");
    } else {
      if (!showSequence && sequence) handleWrong("⏰ Time Up!");
    }
  }, [timer]);

  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
      if (roundTimeoutRef.current) clearTimeout(roundTimeoutRef.current);
      if (achievementTimeoutRef.current) clearTimeout(achievementTimeoutRef.current);
    };
  }, []);

  // ── Correct / Wrong ──────────────────────────────────────────────────────────

  const recordCorrect = () => {
    const newCorrect = correctCount + 1;
    setCorrectCount(newCorrect);
    const newStreak = currentStreak + 1;
    setCurrentStreak(newStreak);
    if (newStreak > bestStreak) setBestStreak(newStreak);
    if (newCorrect === 1)  unlockAchievement("first_correct");
    if (newCorrect === 5)  unlockAchievement("five_correct");
    if (newCorrect === 10) unlockAchievement("ten_correct");
    if (newStreak >= 10)   unlockAchievement("memory_master");
  };

  const recordWrong = () => {
    setWrongCount(p => p + 1);
    setCurrentStreak(0);
  };

  const updateBest = (s) => {
    if (s > bestScore) {
      localStorage.setItem("memory_best_score", String(s));
      setBestScore(s);
    }
  };

  const handleCorrect = () => {
    const newScore = score + level * 10;
    setScore(newScore); updateBest(newScore); recordCorrect();
    setMessage("✅ Correct!");
    if (roundTimeoutRef.current) clearTimeout(roundTimeoutRef.current);
    roundTimeoutRef.current = setTimeout(() => setLevel(p => p + 1), 1000);
  };

  const handleWrong = (msg = "❌ Wrong Answer!") => {
    const rem = lives - 1;
    setLives(rem); setMessage(msg); recordWrong();
    if (rem <= 0) { setMessage("💀 Game Over"); updateBest(score); return; }
    if (roundTimeoutRef.current) clearTimeout(roundTimeoutRef.current);
    roundTimeoutRef.current = setTimeout(startRound, 1500);
  };

  const submitAnswer = () => {
    if (!input.trim()) return;
    input.trim().toUpperCase() === sequence.trim().toUpperCase()
      ? handleCorrect()
      : handleWrong();
  };

  // ── Custom Words ─────────────────────────────────────────────────────────────

  const saveCustomWords = () => {
    const words = customWordsInput.split("\n").map(w => w.trim()).filter(Boolean);
    if (!words.length) { setCustomWarning("⚠ Enter at least one word (one per line)."); return; }
    setCustomWords(words); setCustomWarning(""); setMessage("📝 Custom words saved!");
  };

  // ── Paragraph Recall ─────────────────────────────────────────────────────────

  const startMemorizePhase = (round) => {
    setParagraphPhase("memorize"); setUserParagraphInput(""); setMessage("");
    const displayTime = Math.max(1500, 4500 - level * 100);
    if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
    showTimeoutRef.current = setTimeout(() => {
      setParagraphPhase("recall");
      setTimer(Math.max(8, 18 - Math.floor(level / 2)) + round * 2);
    }, displayTime);
  };

  const startParagraphChallenge = () => {
    if (!savedParagraph.trim()) { setMessage("⚠ Please save a paragraph first."); return; }
    const words = savedParagraph.trim().split(/\s+/).filter(Boolean);
    if (words.length < 2) { setMessage("⚠ Enter a longer paragraph (min 2 words)."); return; }
    setParagraphWords(words); setParagraphRound(1); startMemorizePhase(1);
  };

  const saveParagraph = () => {
    if (!paragraphInput.trim()) { setMessage("⚠ Please enter a paragraph first."); return; }
    setSavedParagraph(paragraphInput.trim());
    setMessage("📝 Paragraph saved! Click 'Start Challenge' to begin.");
  };

  const handleParagraphRoundCorrect = () => {
    const newScore = score + paragraphRound * level * 5;
    setScore(newScore); updateBest(newScore); recordCorrect();
    const isLast = paragraphRound >= paragraphWords.length;
    if (isLast) {
      unlockAchievement("perfect_recall");
      setMessage("✅ Full paragraph recalled! Level up! 🎉");
      if (roundTimeoutRef.current) clearTimeout(roundTimeoutRef.current);
      roundTimeoutRef.current = setTimeout(() => {
        setLevel(p => p + 1); setParagraphRound(1); setParagraphWords([]);
        setSavedParagraph(""); setParagraphInput(""); setParagraphPhase("input");
      }, 1200);
    } else {
      const next = paragraphRound + 1;
      setMessage(`✅ Correct! Now memorize ${next} word(s)...`);
      setParagraphRound(next);
      if (roundTimeoutRef.current) clearTimeout(roundTimeoutRef.current);
      roundTimeoutRef.current = setTimeout(() => startMemorizePhase(next), 900);
    }
  };

  const handleParagraphRoundWrong = (msg = "❌ Wrong Answer!") => {
    const rem = lives - 1;
    setLives(rem); recordWrong();
    if (rem <= 0) { setMessage("💀 Game Over"); updateBest(score); return; }
    setMessage(`${msg} Restarting from word 1…`);
    if (roundTimeoutRef.current) clearTimeout(roundTimeoutRef.current);
    roundTimeoutRef.current = setTimeout(() => { setParagraphRound(1); startMemorizePhase(1); }, 1500);
  };

  const submitParagraphRecall = () => {
    if (!userParagraphInput.trim()) return;
    const expected = paragraphWords.slice(0, paragraphRound).join(" ").toLowerCase().trim();
    const actual   = userParagraphInput.trim().toLowerCase().replace(/\s+/g, " ");
    actual === expected ? handleParagraphRoundCorrect() : handleParagraphRoundWrong();
  };

  // ── Restart ──────────────────────────────────────────────────────────────────

  const restartGame = () => {
    setLevel(1); setScore(0); setLives(3); setMessage(""); setCurrentStreak(0);
    if (mode === "paragraph") {
      setParagraphPhase("input"); setParagraphRound(1); setParagraphWords([]);
    } else {
      startRound();
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────────

  const totalAnswers = correctCount + wrongCount;
  const accuracy = totalAnswers > 0 ? Math.round((correctCount / totalAnswers) * 100) : 0;
  const isGameOver = lives <= 0;

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#FAF6EF] p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-4">

        {/* ── Header ── */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 px-6 py-5 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-widest mb-0.5">
              BharatPariksha · Brain Training
            </p>
            <h1 className="text-3xl font-black text-gray-800">
              🧠 Memory <span className="text-amber-500">Recall</span>
            </h1>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400 font-medium">Best Score</p>
            <p className="text-3xl font-black text-amber-500">{bestScore}</p>
          </div>
        </div>

        {/* ── Achievement Toast ── */}
        {achievementMsg && (
          <div className="bg-amber-50 border-2 border-amber-300 text-amber-800 rounded-2xl px-5 py-3 text-center font-bold text-sm animate-pulse">
            {achievementMsg}
          </div>
        )}

        {/* ── Primary Stats ── */}
        <div className="grid grid-cols-4 gap-3">
          <StatPill label="Level"  value={level}  colorClass="bg-blue-100 text-blue-800" />
          <StatPill label="Score"  value={score}  colorClass="bg-green-100 text-green-800" />
          <StatPill label="Lives"  value={"❤️".repeat(Math.max(lives, 0)) || "💀"} colorClass="bg-red-100 text-red-800" />
          <StatPill label="Streak" value={currentStreak} colorClass="bg-amber-100 text-amber-800" />
        </div>

        {/* ── Secondary Stats ── */}
        <div className="grid grid-cols-4 gap-3">
          <StatPill label="Accuracy"    value={`${accuracy}%`} colorClass="bg-indigo-100 text-indigo-800" />
          <StatPill label="Correct"     value={correctCount}   colorClass="bg-emerald-100 text-emerald-800" />
          <StatPill label="Wrong"       value={wrongCount}     colorClass="bg-rose-100 text-rose-800" />
          <StatPill label="Best Streak" value={bestStreak}     colorClass="bg-cyan-100 text-cyan-800" />
        </div>

        {/* ── Achievements ── */}
        <SectionCard>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Achievements</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(ACHIEVEMENT_LIST).map(([key, label]) => (
              <span
                key={key}
                className={`rounded-full px-3 py-1 text-xs font-bold transition-all ${
                  achievements[key]
                    ? "bg-amber-400 text-white shadow-sm"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                {achievements[key] ? "🏆" : "○"} {label}
              </span>
            ))}
          </div>
        </SectionCard>

        {/* ── Mode Selector ── */}
        <SectionCard>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Game Mode</p>
          <div className="flex flex-wrap gap-2">
            <ModeButton active={mode === "number"}    color="bg-blue-500"   onClick={() => setMode("number")}>   📟 Numbers   </ModeButton>
            <ModeButton active={mode === "word"}      color="bg-purple-500" onClick={() => setMode("word")}>     🔤 Words     </ModeButton>
            <ModeButton active={mode === "custom"}    color="bg-pink-500"   onClick={() => setMode("custom")}>   ✏️ Custom    </ModeButton>
            <ModeButton active={mode === "paragraph"} color="bg-teal-500"   onClick={() => setMode("paragraph")}>📖 Paragraph </ModeButton>
          </div>
        </SectionCard>

        {/* ── Custom Words Input ── */}
        {mode === "custom" && (
          <SectionCard>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Your Custom Words</p>
            <p className="text-xs text-gray-400 mb-2">Enter one word per line</p>
            <textarea
              value={customWordsInput}
              onChange={e => setCustomWordsInput(e.target.value)}
              placeholder={"Python\nTensorFlow\nMachine Learning\nCybersecurity"}
              className="w-full h-28 border-2 border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:border-pink-400 resize-none"
            />
            {customWarning && (
              <p className="text-red-500 text-xs font-semibold mt-2">{customWarning}</p>
            )}
            {customWords.length > 0 && !customWarning && (
              <p className="text-xs text-gray-400 mt-2">{customWords.length} words saved ✓</p>
            )}
            <button
              onClick={saveCustomWords}
              className="mt-3 w-full py-2.5 bg-pink-500 hover:bg-pink-600 text-white font-bold rounded-xl text-sm transition-colors"
            >
              Save Words
            </button>
          </SectionCard>
        )}

        {/* ── Paragraph Mode — Input Phase ── */}
        {mode === "paragraph" && paragraphPhase === "input" && (
          <SectionCard>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Paragraph Recall</p>
            <p className="text-xs text-gray-400 mb-3">
              You'll recall 1 word, then 2, then 3 — building up to the full paragraph.
            </p>
            <textarea
              value={paragraphInput}
              onChange={e => setParagraphInput(e.target.value)}
              placeholder="Machine Learning is a branch of Artificial Intelligence…"
              className="w-full h-28 border-2 border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:border-teal-400 resize-none mb-3"
            />
            {savedParagraph && (
              <p className="text-xs text-gray-400 mb-3">
                ✓ Paragraph saved — {savedParagraph.trim().split(/\s+/).length} words · Level {level} difficulty
              </p>
            )}
            <div className="flex gap-2">
              <button
                onClick={saveParagraph}
                className="flex-1 py-2.5 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-xl text-sm transition-colors"
              >
                Save Paragraph
              </button>
              <button
                onClick={startParagraphChallenge}
                disabled={!savedParagraph.trim()}
                className={`flex-1 py-2.5 font-bold rounded-xl text-sm transition-colors text-white ${
                  savedParagraph.trim() ? "bg-green-500 hover:bg-green-600" : "bg-gray-300 cursor-not-allowed"
                }`}
              >
                Start Challenge
              </button>
            </div>
          </SectionCard>
        )}

        {/* ── Paragraph Mode — Memorise Phase ── */}
        {mode === "paragraph" && paragraphPhase === "memorize" && (
          <SectionCard>
            <div className="flex justify-between items-center mb-4">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Memorise</p>
                <p className="text-sm font-bold text-gray-700">
                  Words 1{paragraphRound > 1 ? `–${paragraphRound}` : ""}
                </p>
              </div>
              <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-3 py-1">
                Round {paragraphRound} / {paragraphWords.length}
              </span>
            </div>
            <div className="bg-blue-50 border-2 border-blue-100 rounded-2xl p-8 text-center">
              <p className="text-3xl font-black text-blue-800 leading-relaxed break-words">
                {paragraphWords.slice(0, paragraphRound).join(" ")}
              </p>
            </div>
          </SectionCard>
        )}

        {/* ── Paragraph Mode — Recall Phase ── */}
        {mode === "paragraph" && paragraphPhase === "recall" && (
          <SectionCard>
            <div className="flex justify-between items-center mb-4">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Recall</p>
                <p className="text-sm font-bold text-gray-700">
                  Type the first {paragraphRound} word{paragraphRound > 1 ? "s" : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-red-400 rounded-full transition-all duration-1000"
                    style={{ width: `${(timer / 15) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-black text-red-500 tabular-nums w-8">{timer}s</span>
              </div>
            </div>
            <textarea
              value={userParagraphInput}
              onChange={e => setUserParagraphInput(e.target.value)}
              placeholder="Type the word(s) here…"
              rows={3}
              className="w-full border-2 border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:border-teal-400 resize-none mb-3"
            />
            <button
              onClick={submitParagraphRecall}
              className="w-full py-3 bg-green-500 hover:bg-green-600 text-white font-black rounded-xl transition-colors"
            >
              Submit Answer
            </button>
          </SectionCard>
        )}

        {/* ── Number / Word / Custom — Memorise Phase ── */}
        {mode !== "paragraph" && !(mode === "custom" && customWarning) && sequence && showSequence && (
          <SectionCard>
            <div className="flex justify-between items-center mb-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Memorise This</p>
              <span className="text-xs bg-gray-100 text-gray-500 rounded-full px-3 py-1">Level {level}</span>
            </div>
            <div className="bg-blue-50 border-2 border-blue-100 rounded-2xl p-10 text-center">
              <p className="text-4xl font-black text-blue-800 tracking-widest break-all">{sequence}</p>
            </div>
          </SectionCard>
        )}

        {/* ── Number / Word / Custom — Recall Phase ── */}
        {mode !== "paragraph" && !(mode === "custom" && customWarning) && sequence && !showSequence && (
          <SectionCard>
            <div className="flex justify-between items-center mb-4">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Your Turn</p>
                <p className="text-sm font-bold text-gray-700">What did you see?</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-red-400 rounded-full transition-all duration-1000"
                    style={{ width: `${(timer / 15) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-black text-red-500 tabular-nums w-8">{timer}s</span>
              </div>
            </div>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submitAnswer()}
              placeholder="Type your answer here…"
              className="w-full border-2 border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:border-blue-400 mb-3"
            />
            <button
              onClick={submitAnswer}
              className="w-full py-3 bg-green-500 hover:bg-green-600 text-white font-black rounded-xl transition-colors"
            >
              Submit Answer
            </button>
          </SectionCard>
        )}

        {/* ── Custom Warning ── */}
        {mode === "custom" && customWarning && (
          <SectionCard>
            <p className="text-red-500 font-semibold text-sm text-center">{customWarning}</p>
          </SectionCard>
        )}

        {/* ── Feedback Message ── */}
        {message && (
          <div className={`rounded-2xl px-5 py-4 text-center font-bold text-lg border-2 ${
            message.includes("✅") || message.includes("📝") ? "bg-green-50 border-green-200 text-green-700" :
            message.includes("❌") || message.includes("⏰") ? "bg-red-50 border-red-200 text-red-700"     :
            message.includes("💀")                           ? "bg-gray-100 border-gray-300 text-gray-700" :
                                                               "bg-amber-50 border-amber-200 text-amber-700"
          }`}>
            {message}
          </div>
        )}

        {/* ── Game Over ── */}
        {isGameOver && (
          <SectionCard className="text-center">
            <p className="text-5xl mb-3">📊</p>
            <h2 className="text-2xl font-black text-gray-800 mb-1">Session Over</h2>
            <div className="grid grid-cols-3 gap-3 my-5">
              <div className="bg-blue-50 rounded-xl p-3">
                <p className="text-xs text-gray-400 mb-1">Final Score</p>
                <p className="text-xl font-black text-blue-700">{score}</p>
              </div>
              <div className="bg-green-50 rounded-xl p-3">
                <p className="text-xs text-gray-400 mb-1">Level</p>
                <p className="text-xl font-black text-green-700">{level}</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-3">
                <p className="text-xs text-gray-400 mb-1">Accuracy</p>
                <p className="text-xl font-black text-amber-700">{accuracy}%</p>
              </div>
            </div>
            <button
              onClick={restartGame}
              className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-black rounded-xl transition-colors text-sm"
            >
              Try Again
            </button>
          </SectionCard>
        )}

        {/* ── Footer ── */}
        <p className="text-center text-xs text-gray-300 pb-2">
          BharatPariksha · Memory Recall · Sharpen your brain daily
        </p>

      </div>
    </div>
  );
}